import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/react-hooks";
import {
  Box,
  Button,
  Collapse,
  LinearProgress,
  Typography,
} from "@mui/material";
import DeleteIcon from "@iconify/icons-mdi/delete";
import FileIcon from "@iconify/icons-mdi/file-document";
import PdfIcon from "@iconify/icons-mdi/file-pdf-box-outline";
import ImageIcon from "@iconify/icons-mdi/image-size-select-actual";
import { InlineIcon } from "@iconify/react";
import axios from "axios";
import { MY_CAFETERIA_DELETE_ASSET } from "my-cafeteria/graphql";
import { useMyCafeteriaRegulation } from "my-cafeteria/hooks";
import {
  OcCard,
  OcDropzoneArea,
  OcPageSideMenu,
  OcPageTransition,
} from "oc/components";
import {
  useOcApi,
  useOcCurrentUserState,
  useOcIsMobile,
  useOcPageSideMenu,
  useOcSnackbar,
} from "oc/hooks";

const myCafeteriaAdminSettingsPageMenuDef = [
  {
    id: "uploads",
    label: "MY_CAFETERIA_UPLOADS",
    roles: ["MyCafeteria.admin"],
  },
];

export const MyCafeteriaAdminSettingsPage = () => {
  const { t } = useTranslation();
  let { employeeCode } = useOcCurrentUserState();
  const isMobile = useOcIsMobile();

  const { apiUrl } = useOcApi();
  const url = `${apiUrl}/upload`;

  const stateId = "myCafeteriaSettingsPageMenuActiveItem";
  const { state: selectedItem } = useOcPageSideMenu({
    id: stateId,
    initialValue: myCafeteriaAdminSettingsPageMenuDef[0].id,
  });

  const { data } = useMyCafeteriaRegulation();
  const [showCollapse, setShowCollapse] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [isUpload, setIsUpload] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  let dataArray = data?.filename?.split("/") || [];
  const filename = dataArray[dataArray?.length - 1] || "";
  const createdAt = new Date(data?.createdAt).toLocaleDateString();

  const { setOpenMessage } = useOcSnackbar();

  const [deleteAsset] = useMutation(MY_CAFETERIA_DELETE_ASSET, {
    onCompleted: () => {
      setOpenMessage({
        type: "success",
        message: t("MY_CAFETERIA_VBK_DELETE_SUCCEEDED"),
      });
    },
    onError: (error) => {
      console.log({ error });
    },
  });

  const showUpload = () => {
    setShowCollapse(true);
  };

  const handleUpload = async () => {
    setIsUpload(true);
    const formData = new FormData();
    formData.append("employeeCode", "");
    formData.append("type", "vbkRegulations");
    formData.append("owner", employeeCode);
    if (file) {
      formData.append("file", file);
    }
    try {
      const config = {
        onUploadProgress: (
          progressEvent: import("axios").AxiosProgressEvent,
        ) => {
          if (progressEvent.total) {
            var percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            setUploadProgress(percentCompleted);
          }
        },
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };
      await axios.post(url, formData, config);
      setIsUpload(false);
      setFile(null);
      setShowCollapse(false);
      setUploadProgress(0);
      setOpenMessage({
        type: "success",
        message: t("MY_CAFETERIA_VBK_UPLOAD_SUCCEEDED"),
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleSelectFile = (file: File | File[]) => {
    let selectedFile: File | undefined;
    if (Array.isArray(file)) {
      selectedFile = file[0];
    } else {
      selectedFile = file;
    }
    if (selectedFile?.size > 10000000) {
      setOpenMessage({
        type: "error",
        message: "OC_FILE_UPLOAD_SIZE_FAILED",
      });
      return;
    }
    setFile(selectedFile || null);
  };

  const abortUpload = () => {
    setIsUpload(false);
  };

  const cancelUpload = () => {
    setIsUpload(false);
    setShowCollapse(false);
    setFile(null);
  };

  const removeVBKRegulation = () => {
    deleteAsset({ variables: { _id: data?._id } });
  };

  return (
    <OcPageTransition>
      <Box>
        <Box
          id="my-hr-admin-system-settings-title"
          height="80px"
          style={{ backgroundColor: "#cc0033" }}
          display="flex"
          flexDirection="row"
          alignItems="center"
          paddingLeft="14px"
        >
          <Box style={{ color: "white", fontSize: "24px" }}>MyCafeteria</Box>
        </Box>
        <Box display="flex" flexDirection={isMobile ? "column" : "row"}>
          <OcPageSideMenu
            stateId={stateId}
            menuDef={myCafeteriaAdminSettingsPageMenuDef}
          />
          <Box
            id="my-hr-admin-system-settings"
            flexGrow={1}
            padding={isMobile ? "16px 0px" : "16px"}
          >
            {selectedItem === "uploads" && (
              <OcCard label={t("MY_CAFETERIA_VBK_REGULATION")}>
                <>
                  <Collapse
                    in={data?.filename ? true : false}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box
                      height="78px"
                      display="flex"
                      flexDirection={"row"}
                      alignItems="center"
                      style={{
                        backgroundColor: "#F1F3F4",
                        border: "1px solid #C6CACC",
                        borderRadius: "8px",
                      }}
                    >
                      <Box width="14px" />
                      <InlineIcon
                        icon={PdfIcon}
                        width="24px"
                        style={{ color: "#4d4d4d" }}
                      />
                      <Box width="14px" />

                      <Box display="flex" flexGrow={1}>
                        <Box>
                          <Typography variant="body1">{filename}</Typography>
                          <Typography
                            variant="caption"
                            style={{
                              color: "#919699",
                            }}
                          >
                            {createdAt}
                          </Typography>
                        </Box>
                      </Box>
                      <Box paddingRight={"16px"}>
                        <InlineIcon
                          id="remove-VBK-regulation-button"
                          icon={DeleteIcon}
                          width="24px"
                          style={{
                            color: "#4d4d4d",
                            cursor: "pointer",
                          }}
                          onClick={removeVBKRegulation}
                        />
                      </Box>
                    </Box>
                    <Box height="16px"></Box>
                  </Collapse>
                  <Collapse in={showCollapse} timeout="auto" unmountOnExit>
                    <Box id="dropzonearea">
                      <OcDropzoneArea
                        dropzoneText={t("OC_DRAG_AND_DROP_FILE")}
                        acceptedFiles={{
                          "application/*": [".pdf"],
                        }}
                        onChange={handleSelectFile}
                        height="81px"
                      />
                      <Collapse in={!!file && !isUpload}>
                        <Box height="32px" />
                        <Box
                          display="flex"
                          flexDirection="row"
                          alignItems={"center"}
                          style={{
                            height: "61px",
                            border: "1px solid #C6CACC",
                            borderRadius: "8px",
                            paddingLeft: "20px",
                          }}
                        >
                          <InlineIcon
                            icon={FileIcon}
                            width="24px"
                            style={{
                              color: "#4d4d4d",
                            }}
                          />
                          <Box width="14px" />
                          <Typography variant="body1">{file?.name}</Typography>
                        </Box>
                      </Collapse>
                      <Collapse in={!!file && isUpload}>
                        <Box
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          style={{
                            height: "102px",
                            backgroundColor: "#C6CACC",
                            padding: "16px",
                            borderRadius: "8px",
                            marginBottom: "32px",
                          }}
                        >
                          <Box>
                            <InlineIcon
                              icon={ImageIcon}
                              width="24px"
                              style={{
                                color: "#4d4d4d",
                              }}
                            />
                          </Box>
                          <Box
                            sx={{
                              width: "100%",
                              padding: "0px 16px",
                            }}
                          >
                            <Typography variant="body1">
                              {file?.name || "nincs kiválasztva"}
                            </Typography>
                            <Box padding={"16px 0px"}>
                              <LinearProgress
                                variant="determinate"
                                value={uploadProgress}
                                color="primary"
                              />
                            </Box>
                            <Typography
                              variant="caption"
                              style={{
                                color: "#919699",
                              }}
                            >
                              {uploadProgress}% done
                            </Typography>
                          </Box>
                          <Box>
                            <InlineIcon
                              icon={DeleteIcon}
                              width="24px"
                              style={{
                                color: "#4d4d4d",
                              }}
                              onClick={abortUpload}
                            />
                          </Box>
                        </Box>
                      </Collapse>
                      <Box height="32px" />
                      <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="flex-end"
                      >
                        <Button
                          id="new-regulation-cancel-button"
                          onClick={cancelUpload}
                          variant="text"
                          color="secondary"
                        >
                          {t("OC_CANCEL")}
                        </Button>
                        <Box width="16px" />
                        <Button
                          id="new-regulation-handle-upload-button"
                          variant="contained"
                          color="primary"
                          disableElevation
                          onClick={handleUpload}
                          disabled={!file}
                        >
                          {t("OC_SAVE")}
                        </Button>
                      </Box>
                    </Box>
                  </Collapse>
                  <Collapse in={!showCollapse} timeout="auto" unmountOnExit>
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="flex-end"
                      padding="16px 0px"
                    >
                      <Button
                        id="new-regulation-show-upload-button"
                        variant="contained"
                        disableElevation
                        color="primary"
                        onClick={showUpload}
                      >
                        {t("MY_CAFETERIA_NEW_REGULATION")}
                      </Button>
                    </Box>
                  </Collapse>
                </>
              </OcCard>
            )}
          </Box>
        </Box>
      </Box>
    </OcPageTransition>
  );
};

export default MyCafeteriaAdminSettingsPage;
